export type Selector<State, Value> = (state: State) => Value

type SelectorValues<Selectors extends readonly Selector<any, any>[]> = {
  [Index in keyof Selectors]: Selectors[Index] extends Selector<any, infer Value> ? Value : never
}

/**
 * Defines a selector from dependency functions or named direct children of state.
 * The named form passes only those children to derive and compares each child
 * by identity, not the freshly picked object. For example:
 * defSelector(['folder', 'uploads'], ({ folder, uploads }: UploadInputs) => ...).
 *
 * The derived value keeps its identity until one of those dependencies changes.
 * This makes the dependency graph visible at module scope and lets view code
 * consume stable values without constructing selectors or caches during render.
 *
 * Dependencies are compared with Object.is. They must therefore preserve
 * identity when their semantic value has not changed: use field accessors,
 * shared constants, or other defSelectors rather than allocating in a
 * dependency. derive must be a pure function of those dependency values.
 *
 * Each selector owns one cache for its lifetime and retains its two most recent
 * dependency snapshots and results. Module-scope selectors consequently share
 * that bounded cache across every store that evaluates them.
 */
export function defSelector<State, const Keys extends readonly (keyof State)[], Result>(
  keys: Keys,
  derive: (state: Pick<State, Keys[number]>) => Result
): Selector<Pick<State, Keys[number]>, Result>
export function defSelector<const Selectors extends readonly Selector<any, any>[], Result>(
  selectors: Selectors,
  derive: (...values: SelectorValues<Selectors>) => Result
): Selector<Parameters<Selectors[0]>[0], Result>
export function defSelector(
  dependencies: readonly (PropertyKey | Selector<any, any>)[],
  derive: (...values: any[]) => any
): Selector<any, any> {
  if (dependencies.length > 0 && typeof dependencies[0] != 'function') {
    const keys = dependencies as readonly PropertyKey[]
    return defSelector(
      keys.map((key) => (state: Record<PropertyKey, unknown>) => state[key]),
      (...values) => derive(Object.fromEntries(keys.map((key, index) => [key, values[index]])))
    )
  }
  const selectors = dependencies as readonly Selector<any, any>[]
  const cache: Array<{ values: unknown[]; result: unknown }> = []

  return (state: unknown) => {
    const values = selectors.map((selector) => selector(state))
    const cachedIndex = cache.findIndex((entry) =>
      values.every((value, index) => Object.is(value, entry.values[index]))
    )

    if (cachedIndex >= 0) {
      const [cached] = cache.splice(cachedIndex, 1)
      cache.push(cached)
      return cached.result
    }

    const result = derive(...values)
    cache.push({ values, result })
    // Reducers commonly derive from previous and next state in alternation.
    // Retaining both snapshots prevents a prev/next comparison from evicting
    // itself and recomputing the same expensive derivation twice per dispatch.
    if (cache.length > 2) cache.shift()
    return result
  }
}
